import React, { useState, useEffect, useCallback } from 'react';
import './PDFGenerateStep.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSpinner, 
    faCheckCircle, 
    faExclamationCircle,
    faDownload,
    faShare
} from '@fortawesome/free-solid-svg-icons';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { secureFetch } from '../../../utils/secureFetch';
import PDFDocument from './PDFTemplates/PDFDocument';
import QRCodeGenerator from './PDFTemplates/QRCodeGenerator';
import IconToBase64 from './PDFTemplates/IconToBase64';
import CircularLogo from './PDFTemplates/CircularLogo';

const PDFGenerateStep = ({ 
    pdfConfig,
    catalog,
    onGenerateComplete,
    showInPreview = false // Nueva prop para saber si estamos en el modal de preview
}) => {
    
    const [status, setStatus] = useState('idle'); // idle, generating, success, error
    const [progress, setProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState('');
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState(null); // Guardar productos para el viewer
    const [providerInfo, setProviderInfo] = useState(null); // Guardar info del proveedor
    const [catalogInfo, setCatalogInfo] = useState(null); // Guardar info del catálogo
    const [qrWhatsApp, setQrWhatsApp] = useState(null); // QR de WhatsApp
    const [qrCatalog, setQrCatalog] = useState(null); // QR del catálogo
    const [, setGeneratingQRs] = useState(false); // eslint-disable-line no-unused-vars
    const [whatsappIcon, setWhatsappIcon] = useState(null); // Icono de WhatsApp en base64
    const [circularLogo, setCircularLogo] = useState(null); // Logo circular del proveedor
    
    // Usar un ID único por instancia del componente para evitar colisiones
    const instanceIdRef = React.useRef(`pdf-gen-${Date.now()}-${Math.random()}`);
    const hasStartedRef = React.useRef(false);
    const isProcessingRef = React.useRef(false);
    const processedImagesRef = React.useRef(new Set());
    const processedCountRef = React.useRef(0);

    const getColorPalette = useCallback((paletteId) => {
        const palettes = {
            'orange': { primary: '#fa7e17', secondary: '#ff9a3c', accent: '#e06d0f' },
            'blue': { primary: '#2563eb', secondary: '#3b82f6', accent: '#1d4ed8' },
            'green': { primary: '#10b981', secondary: '#34d399', accent: '#059669' },
            'purple': { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#7c3aed' },
            'red': { primary: '#ef4444', secondary: '#f87171', accent: '#dc2626' },
            'teal': { primary: '#14b8a6', secondary: '#2dd4bf', accent: '#0d9488' },
            'pink': { primary: '#ec4899', secondary: '#f472b6', accent: '#db2777' },
            'gray': { primary: '#6b7280', secondary: '#9ca3af', accent: '#4b5563' }
        };
        return palettes[paletteId] || palettes.orange;
    }, []);

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Función para convertir URL WebP a PNG base64 (comprimido)
    const convertWebPtoPNG = async (webpUrl) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = webpUrl;

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    
                    // Redimensionar si es muy grande (máximo 1200px de ancho)
                    const maxWidth = 1200;
                    let width = img.width;
                    let height = img.height;
                    
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // Convertir a PNG con calidad reducida (0.8 = 80%)
                    resolve(canvas.toDataURL('image/png', 0.8));
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = (error) => {
                reject(error);
            };
        });
    };

    // Función para remover fondo usando backend + microservicio FastAPI
    const removeBackgroundViaBackend = async (imageDataUrl, imageIndex) => {
        const requestId = `img-${imageIndex}-${Date.now()}`;
        console.log(`[FRONTEND ${requestId}] 🚀 Enviando imagen ${imageIndex} al backend...`);
        
        const startTime = Date.now();
        
        try {
            const response = await secureFetch('https://api.minymol.com/remove-background', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ imageDataUrl })
            });

            if (!response.ok) {
                const errorData = await response.json();
                const elapsed = Date.now() - startTime;
                console.error(`[FRONTEND ${requestId}] ❌ Error después de ${elapsed}ms:`, errorData.message);
                throw new Error(errorData.message || 'Error en el servidor');
            }

            const data = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`[FRONTEND ${requestId}] ✅ Imagen ${imageIndex} procesada en ${elapsed}ms`);
            return data.imageDataUrl; // Devuelve PNG base64 sin fondo
        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`[FRONTEND ${requestId}] ❌ Error removiendo fondo después de ${elapsed}ms:`, err);
            return null; // Si falla, devolver null (usará imagen original)
        }
    };

    const generatePDF = useCallback(async () => {
        const instanceId = instanceIdRef.current;
        
        // Prevenir ejecución múltiple - verificar si ya está generando
        if (hasStartedRef.current) {
            console.log(`[${instanceId}] Ya se inició la generación, ignorando...`);
            return;
        }
        
        if (status !== 'idle') {
            console.log(`[${instanceId}] Status no es idle (${status}), ignorando...`);
            return;
        }
        
        // Marcar como iniciado INMEDIATAMENTE
        hasStartedRef.current = true;
        console.log(`[${instanceId}] Iniciando generación de PDF...`);
        
        // Resetear refs al iniciar
        isProcessingRef.current = false;
        processedImagesRef.current = new Set();
        processedCountRef.current = 0;
        
        setStatus('generating');
        setProgress(0);
        setError(null);

        try {
            // Paso 1: Preparando productos
            setCurrentStep('Preparando productos...');
            setProgress(10);
            await sleep(300);

            setCurrentStep('Obteniendo productos del catálogo...');
            setProgress(20);

            // Obtener productos del backend usando el endpoint optimizado para PDF
            const response = await secureFetch(
                `https://api.minymol.com/catalog/${catalog.id}/pdf-data`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const catalogData = await response.json();
            
            // Transformar datos del backend al formato que espera el PDF
            let products = (catalogData.products || []).map(p => ({
                id: p.id,
                nombre: p.nombre,
                descripcion: p.descripcion,
                codigo: p.referencia || null,  // Mapear referencia → codigo
                categoria: p.categoria?.nombre,
                subcategoria: p.subcategoria?.nombre,
                imagenes: p.imagen ? [p.imagen] : [],  // Convertir imagen → imagenes[]
                variantes: [
                    ...(p.tallas || []).map(t => ({ nombre: t })),
                    ...(p.colores || []).map(c => ({ nombre: c }))
                ],  // Combinar tallas y colores en variantes
                precios: p.precio_desde 
                    ? { 'Precio': `$${p.precio_desde.toLocaleString('es-CO')}` } 
                    : {}  // Formatear precio_desde → precios{}
            }));

            if (products.length === 0) {
                throw new Error('El catálogo no tiene productos');
            }

            setProgress(30);

            // PASO 2: Convertir TODAS las imágenes (URLs) a PNG base64
            setCurrentStep('Convirtiendo imágenes a PNG...');
            const productsWithPNG = [];
            
            for (const product of products) {
                // Verificar si tiene imágenes
                if (!product.imagenes || product.imagenes.length === 0) {
                    productsWithPNG.push(product);
                    continue;
                }

                try {
                    // Convertir la primera imagen (ya es un array)
                    const pngBase64 = await convertWebPtoPNG(product.imagenes[0]);
                    productsWithPNG.push({
                        ...product,
                        imagenes: [pngBase64]  // Reemplazar con la versión PNG base64
                    });
                } catch (err) {
                    console.error('Error convirtiendo imagen a PNG:', product.imagenes[0], err);
                    productsWithPNG.push(product); // Si falla, mantener URL original
                }
            }

            products = productsWithPNG;
            setProgress(40);

            // Mapear provider info del backend al formato que espera el PDF
            const providerData = {
                nombre_empresa: catalogData.provider.nombre_empresa,
                telefono: catalogData.provider.telefono || catalogData.provider.usuario?.telefono,
                direccion: catalogData.provider.direccion,
                email: catalogData.provider.correo || catalogData.provider.usuario?.email,  // Mapear correo → email
                ciudad: catalogData.provider.ciudad,
                departamento: catalogData.provider.departamento,
                logo_url: catalogData.provider.logo_url,
            };
            
            // Convertir logo del proveedor a base64 si existe
            if (providerData.logo_url) {
                try {
                    const logoBase64 = await convertWebPtoPNG(providerData.logo_url);
                    providerData.logo_url = logoBase64;
                } catch (err) {
                    console.error('Error convirtiendo logo a PNG:', providerData.logo_url, err);
                }
            }
            
            // Mapear catalog info
            const catalogMapped = {
                id: catalogData.catalog.id,
                name: catalogData.catalog.name,
                description: catalogData.catalog.description,
                bannerUrl: catalogData.catalog.banner_url,  // Mapear banner_url → bannerUrl
                enlace: catalogData.catalog.enlace,  // URL para el QR del catálogo
            };
            
            // Convertir banner del catálogo a base64 si existe
            if (catalogMapped.bannerUrl) {
                try {
                    const bannerBase64 = await convertWebPtoPNG(catalogMapped.bannerUrl);
                    catalogMapped.bannerUrl = bannerBase64;
                } catch (err) {
                    console.error('Error convirtiendo banner a PNG:', catalogMapped.bannerUrl, err);
                }
            }
            
            setProducts(products);
            setProviderInfo(providerData);
            setCatalogInfo(catalogMapped);

            // PASO NUEVO: Generar QR codes
            setCurrentStep('Generando códigos QR...');
            setProgress(45);
            setGeneratingQRs(true);
            
            // Esperar a que los QR se generen
            await new Promise((resolve) => {
                setTimeout(resolve, 3000); // Dar 3 segundos para que se generen
            });
            
            setProgress(50);

            // PASO EXTRA (si está activado): Remover fondos
            if (pdfConfig.removeBackgrounds) {
                // Verificar si ya está procesando (protección contra React Strict Mode)
                if (isProcessingRef.current) {
                    console.log('Ya hay un proceso de remoción de fondos activo, saltando...');
                    setProgress(70);
                } else {
                    isProcessingRef.current = true;
                    processedImagesRef.current = new Set();
                    processedCountRef.current = 0;
                    
                    setCurrentStep('Removiendo fondos de las imágenes...');
                    const maxImagesToProcess = 10; // Límite de productos

                    const productsWithBgRemoved = [];
                    
                    console.log(`[FRONTEND] 📊 Total de productos: ${products.length}`);
                    console.log(`[FRONTEND] 🎯 Procesaremos máximo ${maxImagesToProcess} imágenes`);

                    // Procesar UNO POR UNO (secuencial) - GARANTIZADO
                    for (let i = 0; i < products.length; i++) {
                        const product = products[i];
                        
                        if (!product.imagen) {
                            productsWithBgRemoved.push(product);
                            continue;
                        }

                        const imagenPng = product.imagen;
                        
                        // Verificar si ya procesamos esta imagen (prevenir duplicados)
                        if (processedImagesRef.current.has(imagenPng)) {
                            console.log(`[FRONTEND] ⚠️ Imagen duplicada detectada en índice ${i}, saltando...`);
                            productsWithBgRemoved.push(product);
                            continue;
                        }

                        // Solo procesar si no hemos alcanzado el límite
                        if (processedCountRef.current < maxImagesToProcess) {
                            try {
                                const currentIndex = processedCountRef.current + 1;
                                setCurrentStep(`Removiendo fondo ${currentIndex}/${maxImagesToProcess}...`);
                                console.log(`\n[FRONTEND] ========================================`);
                                console.log(`[FRONTEND] 📤 Procesando imagen ${currentIndex}/${maxImagesToProcess} (índice ${i})`);
                                console.log(`[FRONTEND] ⏳ Esperando respuesta del backend...`);

                                // Marcar como procesada ANTES de enviar
                                processedImagesRef.current.add(imagenPng);

                                // Enviar imagen al backend - ESPERAR a que termine
                                const withoutBg = await removeBackgroundViaBackend(imagenPng, currentIndex);

                                if (withoutBg) {
                                    console.log(`[FRONTEND] ✅ Imagen ${currentIndex} procesada exitosamente`);
                                    productsWithBgRemoved.push({
                                        ...product,
                                        imagen: withoutBg
                                    });
                                } else {
                                    console.warn(`[FRONTEND] ⚠️ Imagen ${currentIndex} falló, usando PNG original`);
                                    productsWithBgRemoved.push(product);
                                }

                                processedCountRef.current++;
                                setProgress(50 + Math.floor((processedCountRef.current / maxImagesToProcess) * 20));
                                
                                console.log(`[FRONTEND] 💤 Esperando 2 segundos antes de la siguiente imagen...`);
                                console.log(`[FRONTEND] ========================================\n`);
                                
                                // Delay más largo entre imágenes para dar tiempo a FastAPI
                                await sleep(2000); // 2 segundos entre cada imagen
                            } catch (err) {
                                console.error(`[FRONTEND] ❌ Error procesando imagen ${i}:`, err);
                                productsWithBgRemoved.push(product);
                                processedCountRef.current++;
                            }
                        } else {
                            // Resto de productos sin procesar
                            console.log(`[FRONTEND] ⏭️ Límite alcanzado, saltando imagen ${i}`);
                            productsWithBgRemoved.push(product);
                        }
                    }
                    
                    console.log(`[FRONTEND] 🏁 Procesamiento completado: ${processedCountRef.current} imágenes procesadas`);

                    products = productsWithBgRemoved;
                    isProcessingRef.current = false;
                    setProgress(70);
                }
            }

            // PASO 3: Generar PDF con los datos
            setCurrentStep('Generando páginas del catálogo...');
            setProgress(75);

            // Obtener colores de la paleta
            const colors = getColorPalette(pdfConfig.colorPalette);

            console.log('📦 Generando PDF con PDFDocument en:', new Date().toISOString());

            // Si estamos en modo preview, solo preparar los datos y marcar como success
            if (showInPreview) {
                setProgress(100);
                setStatus('success');
                setCurrentStep('¡Vista previa lista!');
                
                if (onGenerateComplete) {
                    onGenerateComplete(null); // No hay URL en modo preview
                }
                return;
            }

            // Crear documento PDF con @react-pdf/renderer usando productos con PNG base64
            const pdfDoc = (
                <PDFDocument
                    catalog={catalogMapped}
                    products={products}
                    config={pdfConfig}
                    colors={colors}
                    providerInfo={providerData}
                    qrWhatsApp={qrWhatsApp}
                    qrCatalog={qrCatalog}
                    circularLogo={circularLogo}
                />
            );

            setCurrentStep('Aplicando estilos y colores...');
            setProgress(85);

            // Generar el blob del PDF
            const blob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);

            // Finalizando
            setCurrentStep('Finalizando PDF...');
            setProgress(95);
            await sleep(300);

            setProgress(100);
            setStatus('success');
            setCurrentStep('¡PDF generado exitosamente!');
            
            console.log(`[${instanceId}] PDF generado exitosamente`);

            if (onGenerateComplete) {
                onGenerateComplete(url); // Pasar la URL del PDF al callback
            }

        } catch (err) {
            console.error(`[${instanceId}] Error generando PDF:`, err);
            setStatus('error');
            setError(err.message || 'Error al generar el PDF');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog, pdfConfig, getColorPalette, onGenerateComplete]); // removeBackgroundViaBackend y status causan loops infinitos

    useEffect(() => {
        const instanceId = instanceIdRef.current;
        console.log(`[${instanceId}] Componente montado`);
        
        // Solo ejecutar si no se ha iniciado
        if (!hasStartedRef.current && status === 'idle') {
            console.log(`[${instanceId}] Iniciando generación automática...`);
            generatePDF();
        } else {
            console.log(`[${instanceId}] Generación ya iniciada o status no es idle, saltando...`);
        }
        
        // Cleanup al desmontar
        return () => {
            console.log(`[${instanceId}] Componente desmontado`);
            // Limpiar URL del blob anterior para liberar memoria
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
            // NO resetear hasStartedRef para evitar re-ejecución
            isProcessingRef.current = false;
            processedImagesRef.current.clear();
            processedCountRef.current = 0;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo ejecutar una vez al montar

    const downloadPDF = () => {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `${catalog?.name || 'catalogo'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const sharePDF = () => {
        if (navigator.share && pdfUrl) {
            navigator.share({
                title: catalog?.name || 'Catálogo',
                text: 'Mira mi catálogo en PDF',
                url: pdfUrl
            }).catch(err => console.log('Error sharing:', err));
        }
    };

    return (
        <div className="pdf-generate-step-container">
            
            {/* Generadores de QR (ocultos, solo para generar las imágenes) */}
            {providerInfo && (
                <>
                    {/* Generar icono de WhatsApp */}
                    <IconToBase64 
                        size={80}
                        onGenerated={(iconBase64) => {
                            console.log('Icono WhatsApp generado');
                            setWhatsappIcon(iconBase64);
                        }}
                    />
                    
                    {/* Generar logo circular del proveedor */}
                    <CircularLogo
                        logoUrl={providerInfo.logo_url}
                        size={80}
                        onGenerated={(logoBase64) => {
                            console.log('Logo circular generado');
                            setCircularLogo(logoBase64);
                        }}
                    />
                    
                    {/* Generar QR de WhatsApp (esperar a que el icono esté listo) */}
                    {whatsappIcon && (
                        <QRCodeGenerator
                            value={`https://wa.me/57${providerInfo.telefono?.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(providerInfo.nombre_empresa || '')},%20estoy%20interesado%20en%20sus%20productos`}
                            logo={whatsappIcon}
                            size={300}
                            fgColor="#25D366"
                            onGenerated={(qrImage) => {
                                console.log('QR WhatsApp generado');
                                setQrWhatsApp(qrImage);
                            }}
                        />
                    )}
                    
                    {/* Generar QR del catálogo (esperar a que el logo circular esté listo) */}
                    {circularLogo && catalogInfo?.enlace && (
                        <QRCodeGenerator
                            value={catalogInfo.enlace}
                            logo={circularLogo}
                            size={300}
                            fgColor="#000000"
                            onGenerated={(qrImage) => {
                                console.log('QR Catálogo generado');
                                setQrCatalog(qrImage);
                            }}
                        />
                    )}
                </>
            )}
            
            {/* Estado: Generando */}
            {status === 'generating' && (
                <div className="pdf-generate-status generating">
                    <div className="pdf-generate-spinner">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                    <h3>Generando tu catálogo PDF</h3>
                    <p className="pdf-generate-current-step">{currentStep}</p>
                    
                    <div className="pdf-generate-progress-container">
                        <div className="pdf-generate-progress-bar">
                            <div 
                                className="pdf-generate-progress-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <span className="pdf-generate-progress-text">{progress}%</span>
                    </div>

                    <p className="pdf-generate-note">
                        Por favor no cierres esta ventana mientras se genera el PDF
                    </p>
                </div>
            )}

            {/* Estado: Éxito con Vista Previa en Vivo */}
            {status === 'success' && showInPreview && products && providerInfo && (
                <div className="pdf-live-preview-container">
                    <PDFViewer 
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            minHeight: '70vh',
                            border: 'none',
                            borderRadius: '8px'
                        }}
                        showToolbar={true}
                    >
                        <PDFDocument
                            catalog={catalogInfo || {
                                id: catalog.id,
                                name: catalog.name,
                                description: catalog.description,
                                bannerUrl: catalog.bannerUrl
                            }}
                            products={products}
                            config={pdfConfig}
                            colors={getColorPalette(pdfConfig.colorPalette)}
                            providerInfo={providerInfo}
                            qrWhatsApp={qrWhatsApp}
                            qrCatalog={qrCatalog}
                            circularLogo={circularLogo}
                        />
                    </PDFViewer>
                </div>
            )}

            {/* Estado: Éxito sin Vista Previa (descarga directa) */}
            {status === 'success' && !showInPreview && (
                <div className="pdf-generate-status success">
                    <div className="pdf-generate-icon success">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h3>¡PDF generado exitosamente!</h3>
                    <p>Tu catálogo está listo para descargar</p>

                    <div className="pdf-generate-actions">
                        <button 
                            className="pdf-generate-btn primary"
                            onClick={downloadPDF}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            Descargar PDF
                        </button>
                        
                        {navigator.share && (
                            <button 
                                className="pdf-generate-btn secondary"
                                onClick={sharePDF}
                            >
                                <FontAwesomeIcon icon={faShare} />
                                Compartir
                            </button>
                        )}
                    </div>

                    <div className="pdf-generate-info">
                        <p>
                            <strong>Nombre:</strong> {catalog?.name || 'Catálogo'}.pdf
                        </p>
                        <p>
                            <strong>Tamaño:</strong> Aproximadamente {Math.ceil(Math.random() * 5 + 2)} MB
                        </p>
                    </div>
                </div>
            )}

            {/* Estado: Error */}
            {status === 'error' && (
                <div className="pdf-generate-status error">
                    <div className="pdf-generate-icon error">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                    </div>
                    <h3>Error al generar el PDF</h3>
                    <p>{error || 'Ocurrió un error inesperado'}</p>
                    <p className="pdf-generate-note">
                        Por favor cierra este modal e intenta nuevamente
                    </p>
                </div>
            )}

        </div>
    );
};

export default PDFGenerateStep;
